import React from 'react';
import PropTypes from 'prop-types';
import {
  formatAssetTokenLabel,
  getAssetPillClass,
  normalizeAssetToken,
  REQUIREMENT_DISPLAY_ORDER,
} from '../../utils/assetTokens';
import './AssetPills.css';

const sortTokensByDisplayOrder = (entries) => {
  if (!Array.isArray(entries) || entries.length <= 1) {
    return entries;
  }
  const orderIndex = new Map(REQUIREMENT_DISPLAY_ORDER.map((token, index) => [token, index]));
  return [...entries].sort((a, b) => {
    const orderA = orderIndex.has(a.normalized) ? orderIndex.get(a.normalized) : orderIndex.size + 1;
    const orderB = orderIndex.has(b.normalized) ? orderIndex.get(b.normalized) : orderIndex.size + 1;
    if (orderA === orderB) {
      return a.label.localeCompare(b.label);
    }
    return orderA - orderB;
  });
};

const mapAssetsToEntries = (assets = [], dedupe = true) => {
  if (!Array.isArray(assets)) {
    return [];
  }
  const seen = new Set();
  return assets.reduce((acc, raw) => {
    if (!raw || typeof raw !== 'string') {
      return acc;
    }
    const key = raw.trim().toLowerCase();
    if (dedupe && seen.has(key)) {
      return acc;
    }
    seen.add(key);
    const normalized = normalizeAssetToken(raw) || null;
    const label = formatAssetTokenLabel(raw);
    acc.push({
      label,
      normalized,
      pillClass: normalized ? getAssetPillClass(normalized) : 'pill--default',
    });
    return acc;
  }, []);
};

export default function AssetPills({
  assets = [],
  ariaLabel,
  className = '',
  emptyText = null,
  sortByDisplayOrder = true,
  dedupe = true,
  tooltipTemplate = null,
}) {
  const entries = sortByDisplayOrder
    ? sortTokensByDisplayOrder(mapAssetsToEntries(assets, dedupe))
    : mapAssetsToEntries(assets, dedupe);

  if (!entries.length) {
    if (!emptyText) {
      return null;
    }
    return (
      <div className={`asset-pills-empty ${className}`.trim()}>
        {emptyText}
      </div>
    );
  }

  const getTooltip = (label) => {
    if (!tooltipTemplate) return undefined;
    return tooltipTemplate.replace('{asset}', label.toLowerCase());
  };

  return (
    <div
      className={`asset-pills-row backtest-meta-pill-row ${className}`.trim()}
      role="list"
      aria-label={ariaLabel}
    >
      {entries.map((entry) => (
        <span
          key={`${entry.pillClass}-${entry.label}`}
          className={`backtest-meta-pill ${entry.pillClass}`}
          role="listitem"
          title={getTooltip(entry.label)}
        >
          {entry.label}
        </span>
      ))}
    </div>
  );
}

AssetPills.propTypes = {
  assets: PropTypes.arrayOf(PropTypes.string),
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  emptyText: PropTypes.string,
  sortByDisplayOrder: PropTypes.bool,
  dedupe: PropTypes.bool,
  tooltipTemplate: PropTypes.string,
};
